import React, { useEffect, useMemo, useRef, useState } from 'react';

import { api } from './api';
import { useAuth } from './auth';
import { useI18n } from './i18n';
import { MediaType, QuestionScope, QuestionType, SupportedLanguage } from './QuestionPlayer';
import { getRuntimeConfigValue } from './runtimeConfig';
import './StudyPage.css';

type QuestionTagOption = {
  id: string;
  slug: string;
  name: string;
};

type Translation = {
  lang: SupportedLanguage;
  question_text: string;
  answer_a: string | null;
  answer_b: string | null;
  answer_c: string | null;
};

type StudyQuestion = {
  id: string;
  number: number;
  type: QuestionType;
  scope: QuestionScope;
  correct_answer: AnswerValue;
  points: number;
  media_file: string | null;
  media_type: MediaType | null;
  translation: Translation | null;
  topic_tag: QuestionTagOption;
};

type ProgressBucket = {
  total: number;
  correct_once: number;
  not_correct_once: number;
};

type StudyTagProgressResponse = {
  category: string;
  all_tags: ProgressBucket;
  tags: Array<{
    tag: QuestionTagOption;
    total: number;
    correct_once: number;
    not_correct_once: number;
  }>;
};

type AnswerValue = 'T' | 'N' | 'A' | 'B' | 'C';
type AnswerResult = 'correct' | 'incorrect' | null;

const MEDIA_BASE_URL = (getRuntimeConfigValue('REACT_APP_MEDIA_BASE_URL') || 'https://pub-2cdeeb37268f46c7b8d1013e3ab56448.r2.dev')
  .replace(/\/+$/, '');
const STUDY_BATCH_SIZE = 24;
const QUEUE_REFILL_THRESHOLD = 8;
const CORRECT_ADVANCE_DELAY_MS = 650;
const STUDY_AD_INTERVAL = 12;
const STUDY_AD_SECONDS = 5;

function getProgressPercent(bucket: ProgressBucket | undefined) {
  if (!bucket?.total) {
    return 0;
  }
  return Math.round((bucket.correct_once / bucket.total) * 100);
}

function buildMediaUrl(mediaFile: string | null) {
  if (!mediaFile) {
    return '';
  }
  return `${MEDIA_BASE_URL}/videos/${mediaFile.replace(/\.wmv$/i, '.mp4')}`;
}

function inferMediaType(question: StudyQuestion): MediaType | null {
  if (!question.media_file) {
    return null;
  }
  if (question.media_type === 'VIDEO' || question.media_type === 'IMAGE') {
    return question.media_type;
  }

  const normalized = question.media_file.toLowerCase();
  if (
    normalized.endsWith('.jpg') ||
    normalized.endsWith('.jpeg') ||
    normalized.endsWith('.png') ||
    normalized.endsWith('.gif') ||
    normalized.endsWith('.webp')
  ) {
    return 'IMAGE';
  }
  return 'VIDEO';
}

function getTranslation(question: StudyQuestion, language: SupportedLanguage) {
  return question.translation && question.translation.lang === language ? question.translation : question.translation;
}

function formatAnswerLabel(question: StudyQuestion, translation: Translation | undefined, answer: AnswerValue, yes: string, no: string) {
  if (question.type === 'TAK_NIE') {
    return answer === 'T' ? yes : no;
  }
  if (answer === 'A') {
    return translation?.answer_a || '';
  }
  if (answer === 'B') {
    return translation?.answer_b || '';
  }
  return translation?.answer_c || '';
}

export default function StudyPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { language, t } = useI18n();
  const [tags, setTags] = useState<QuestionTagOption[]>([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isFetchingQuestions, setIsFetchingQuestions] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [queue, setQueue] = useState<StudyQuestion[]>([]);
  const [tagProgress, setTagProgress] = useState<StudyTagProgressResponse | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerValue | null>(null);
  const [answerResult, setAnswerResult] = useState<AnswerResult>(null);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [showStudyAd, setShowStudyAd] = useState(false);
  const [studyAdSecondsLeft, setStudyAdSecondsLeft] = useState(STUDY_AD_SECONDS);
  const [advanceAfterAd, setAdvanceAfterAd] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const advanceTimeoutRef = useRef<number | null>(null);

  const copy = t.study;
  const currentQuestion = queue[currentIndex] || null;
  const currentTranslation = currentQuestion ? getTranslation(currentQuestion, language) : undefined;
  const currentMediaUrl = currentQuestion ? buildMediaUrl(currentQuestion.media_file) : '';
  const currentMediaType = currentQuestion ? inferMediaType(currentQuestion) : null;
  const visiblePreloadQuestions = queue.slice(currentIndex + 1, currentIndex + 7);
  const isFreeUser = user?.account_tier === 'FREE';
  const progressByTag = useMemo(() => {
    const map: Record<string, ProgressBucket> = {};
    tagProgress?.tags.forEach((item) => {
      map[item.tag.slug] = {
        total: item.total,
        correct_once: item.correct_once,
        not_correct_once: item.not_correct_once,
      };
    });
    return map;
  }, [tagProgress]);

  const answerOptions = useMemo(() => {
    if (!currentQuestion) {
      return [];
    }
    if (currentQuestion.type === 'TAK_NIE') {
      return [
        { value: 'T' as const, label: t.common.yes },
        { value: 'N' as const, label: t.common.no },
      ];
    }
    return [
      { value: 'A' as const, label: currentTranslation?.answer_a || '' },
      { value: 'B' as const, label: currentTranslation?.answer_b || '' },
      { value: 'C' as const, label: currentTranslation?.answer_c || '' },
    ];
  }, [currentQuestion, currentTranslation, t.common.no, t.common.yes]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTags([]);
      setSelectedTag('');
      setQueue([]);
      setIsStarted(false);
      return;
    }

    async function loadTags() {
      setIsLoadingOptions(true);
      setFetchError('');

      try {
        const tagResponse = await api.get<QuestionTagOption[]>('/question-tags/');
        setTags(tagResponse.data);
      } catch (error) {
        setTags([]);
        setFetchError(copy.categoriesErrorFallback);
      } finally {
        setIsLoadingOptions(false);
      }
    }

    void loadTags();
  }, [copy.categoriesErrorFallback, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user?.preferred_exam_category) {
      setTagProgress(null);
      return;
    }

    void loadTagProgress();
  }, [isAuthenticated, user?.preferred_exam_category]);

  useEffect(() => {
    if (!currentQuestion || currentMediaType !== 'VIDEO' || !videoRef.current) {
      return;
    }

    videoRef.current.load();
    videoRef.current.play().catch(() => undefined);
  }, [currentQuestion?.id, currentMediaType]);

  useEffect(() => {
    if (!isStarted || isFetchingQuestions || queue.length === 0) {
      return;
    }
    if (queue.length - currentIndex <= QUEUE_REFILL_THRESHOLD) {
      void loadQuestionBatch('append');
    }
  }, [currentIndex, isFetchingQuestions, isStarted, queue.length]);

  useEffect(() => {
    if (!showStudyAd || studyAdSecondsLeft <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setStudyAdSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [showStudyAd, studyAdSecondsLeft]);

  useEffect(() => {
    return () => {
      if (advanceTimeoutRef.current !== null) {
        window.clearTimeout(advanceTimeoutRef.current);
      }
    };
  }, []);

  async function loadQuestionBatch(mode: 'replace' | 'append', tagSlug = selectedTag) {
    if (!user?.preferred_exam_category) {
      return;
    }

    setIsFetchingQuestions(true);
    setFetchError('');

    try {
      const response = await api.get<StudyQuestion[]>('/study/session/', {
        params: {
          language,
          tag: tagSlug || undefined,
          limit: STUDY_BATCH_SIZE,
        },
      });

      setQueue((current) => (mode === 'replace' ? response.data : [...current, ...response.data]));
      if (mode === 'replace') {
        setCurrentIndex(0);
      }
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      setFetchError(typeof apiError === 'string' ? apiError : copy.fetchErrorFallback);
    } finally {
      setIsFetchingQuestions(false);
    }
  }

  async function loadTagProgress() {
    if (!user?.preferred_exam_category) {
      return;
    }

    setIsLoadingProgress(true);

    try {
      const response = await api.get<StudyTagProgressResponse>('/study/tag-progress/', {
        params: {},
      });
      setTagProgress(response.data);
    } catch (error) {
      setTagProgress(null);
    } finally {
      setIsLoadingProgress(false);
    }
  }

  async function startStudy(tagSlug = selectedTag) {
    if (!user?.preferred_exam_category) {
      return;
    }

    if (advanceTimeoutRef.current !== null) {
      window.clearTimeout(advanceTimeoutRef.current);
      advanceTimeoutRef.current = null;
    }

    setIsStarted(true);
    setSelectedTag(tagSlug);
    setSelectedAnswer(null);
    setAnswerResult(null);
    setAnsweredCount(0);
    setCorrectCount(0);
    setSubmitError('');
    setShowStudyAd(false);
    setAdvanceAfterAd(false);
    setStudyAdSecondsLeft(STUDY_AD_SECONDS);
    await loadQuestionBatch('replace', tagSlug);
    await loadTagProgress();
  }

  function showTagSelector() {
    setIsStarted(false);
    setQueue([]);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setAnswerResult(null);
    setSubmitError('');
    if (advanceTimeoutRef.current !== null) {
      window.clearTimeout(advanceTimeoutRef.current);
      advanceTimeoutRef.current = null;
    }
  }

  function goToNextQuestion() {
    if (advanceTimeoutRef.current !== null) {
      window.clearTimeout(advanceTimeoutRef.current);
      advanceTimeoutRef.current = null;
    }

    setSelectedAnswer(null);
    setAnswerResult(null);
    setSubmitError('');

    if (currentIndex + 1 < queue.length) {
      setCurrentIndex((value) => value + 1);
      return;
    }

    void loadQuestionBatch('append');
  }

  async function handleAnswer(answer: AnswerValue) {
    if (!currentQuestion || answerResult) {
      return;
    }

    const correct = answer === currentQuestion.correct_answer;
    const nextAnsweredCount = answeredCount + 1;
    setSelectedAnswer(answer);
    setAnswerResult(correct ? 'correct' : 'incorrect');
    setAnsweredCount(nextAnsweredCount);
    if (correct) {
      setCorrectCount((value) => value + 1);
    }

    try {
      await api.post('/study/progress-answers/', {
        answers: [{ id: currentQuestion.id, correct }],
      });
    } catch (error) {
      setSubmitError(copy.submitErrorFallback);
    }
    void loadTagProgress();

    const shouldShowAd = Boolean(isFreeUser && nextAnsweredCount > 0 && nextAnsweredCount % STUDY_AD_INTERVAL === 0);
    if (shouldShowAd) {
      setStudyAdSecondsLeft(STUDY_AD_SECONDS);
      setAdvanceAfterAd(correct);
      setShowStudyAd(true);
      return;
    }

    if (correct) {
      advanceTimeoutRef.current = window.setTimeout(goToNextQuestion, CORRECT_ADVANCE_DELAY_MS);
    }
  }

  function closeStudyAd() {
    setShowStudyAd(false);
    setStudyAdSecondsLeft(STUDY_AD_SECONDS);

    if (advanceAfterAd) {
      setAdvanceAfterAd(false);
      goToNextQuestion();
    }
  }

  return (
    <div className="study-page">
      <div className="study-page__shell">
        <section className="study-page__header">
          <div>
            <h1>{copy.title}</h1>
            <p>{copy.description}</p>
          </div>
          <div className="study-page__stats" aria-live="polite">
            <div>
              <span>{copy.answered}</span>
              <strong>{answeredCount}</strong>
            </div>
            <div>
              <span>{copy.correctCount}</span>
              <strong>{correctCount}</strong>
            </div>
            <div>
              <span>{copy.remaining}</span>
              <strong>{Math.max(0, queue.length - currentIndex - 1)}</strong>
            </div>
          </div>
        </section>

        {isLoading ? (
          <section className="study-page__message">{copy.loading}</section>
        ) : !isAuthenticated ? (
          <section className="study-page__message">{copy.loginRequired}</section>
        ) : isLoadingOptions ? (
          <section className="study-page__message">{copy.loadingCategories}</section>
        ) : (
          <>
            {isStarted ? (
              <section className="study-page__controls">
                <button type="button" onClick={() => void startStudy(selectedTag)} disabled={!user?.preferred_exam_category || isFetchingQuestions}>
                  {isFetchingQuestions ? copy.loadingQuestions : copy.restart}
                </button>
                <button type="button" className="study-page__secondary-button" onClick={showTagSelector}>
                  {copy.tagLabel}
                </button>
              </section>
            ) : null}

            {fetchError ? <section className="study-page__error">{fetchError}</section> : null}
            {submitError ? <section className="study-page__error">{submitError}</section> : null}

            {!isStarted ? (
              <section className="study-page__tag-panel">
                <div className="study-page__tag-heading">
                  <div>
                    <span>{copy.tagLabel}</span>
                    <h2>{copy.chooseScopeTitle}</h2>
                  </div>
                  {isLoadingProgress ? <p>{copy.loadingCategories}</p> : null}
                </div>

                <div className="study-page__tag-grid">
                  <TagStudyButton
                    disabled={!user?.preferred_exam_category || isFetchingQuestions || !tagProgress?.all_tags.total}
                    isLoading={isFetchingQuestions && selectedTag === ''}
                    label={copy.allTags}
                    onClick={() => void startStudy('')}
                    progress={tagProgress?.all_tags}
                  />
                  {tags.map((tag) => (
                    <TagStudyButton
                      key={tag.id}
                      disabled={!user?.preferred_exam_category || isFetchingQuestions || !progressByTag[tag.slug]?.total}
                      isLoading={isFetchingQuestions && selectedTag === tag.slug}
                      label={t.tagLabels[tag.slug] || tag.name}
                      onClick={() => void startStudy(tag.slug)}
                      progress={progressByTag[tag.slug]}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <div className="content-layout">
                <StudyCard
                  answerOptions={answerOptions}
                  answerResult={answerResult}
                  copy={copy}
                  currentMediaType={currentMediaType}
                  currentMediaUrl={currentMediaUrl}
                  currentQuestion={currentQuestion}
                  currentTranslation={currentTranslation}
                  onAnswer={handleAnswer}
                  onNext={goToNextQuestion}
                  selectedAnswer={selectedAnswer}
                  videoRef={videoRef}
                />
              </div>
            )}

            <div className="study-page__preload" aria-hidden="true">
              {visiblePreloadQuestions.map((question) => {
                const mediaUrl = buildMediaUrl(question.media_file);
                const mediaType = inferMediaType(question);
                if (!mediaUrl || !mediaType) {
                  return null;
                }
                return mediaType === 'VIDEO' ? (
                  <video key={question.id} src={mediaUrl} preload="auto" muted playsInline />
                ) : (
                  <img key={question.id} src={mediaUrl} alt="" />
                );
              })}
            </div>

            {showStudyAd ? (
              <div className="study-page__ad-modal" role="dialog" aria-modal="true">
                <div className="study-page__ad-dialog">
                  <h2>{copy.adTitle}</h2>
                  <p>{copy.adText}</p>
                  <button
                    type="button"
                    className="study-page__ad-button"
                    disabled={studyAdSecondsLeft > 0}
                    onClick={closeStudyAd}
                  >
                    {studyAdSecondsLeft > 0
                      ? `${copy.adCountdown} ${studyAdSecondsLeft}`
                      : copy.adContinue}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

type TagStudyButtonProps = {
  disabled: boolean;
  isLoading: boolean;
  label: string;
  onClick: () => void;
  progress: ProgressBucket | undefined;
};

function TagStudyButton({ disabled, isLoading, label, onClick, progress }: TagStudyButtonProps) {
  const { t } = useI18n();
  const percent = getProgressPercent(progress);
  const total = progress?.total || 0;
  const correctOnce = progress?.correct_once || 0;
  const notCorrectOnce = progress?.not_correct_once || 0;

  return (
    <button type="button" className="study-page__tag-button" onClick={onClick} disabled={disabled}>
      <span className="study-page__tag-name">{label}</span>
      <span className="study-page__tag-summary">
        {isLoading ? t.study.loadingQuestions : `${correctOnce}/${total} ${t.study.progressCorrect}`}
      </span>
      <span className="study-page__tag-progress" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </span>
      <span className="study-page__tag-footer">
        <strong>{percent}%</strong>
        <span>{notCorrectOnce} {t.study.progressNotCorrect}</span>
      </span>
    </button>
  );
}

type StudyCardProps = {
  answerOptions: Array<{ value: AnswerValue; label: string }>;
  answerResult: AnswerResult;
  copy: ReturnType<typeof useI18n>['t']['study'];
  currentMediaType: MediaType | null;
  currentMediaUrl: string;
  currentQuestion: StudyQuestion | null;
  currentTranslation: Translation | null | undefined;
  onAnswer: (answer: AnswerValue) => void;
  onNext: () => void;
  selectedAnswer: AnswerValue | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
};

function StudyCard({
  answerOptions,
  answerResult,
  copy,
  currentMediaType,
  currentMediaUrl,
  currentQuestion,
  currentTranslation,
  onAnswer,
  onNext,
  selectedAnswer,
  videoRef,
}: StudyCardProps) {
  const { t } = useI18n();

  if (!currentQuestion || !currentTranslation) {
    return <section className="study-card study-card--empty">{copy.noQuestionLoaded}</section>;
  }

  const correctAnswerLabel = formatAnswerLabel(
    currentQuestion,
    currentTranslation,
    currentQuestion.correct_answer,
    t.common.yes,
    t.common.no,
  );

  return (
    <section className="study-card">
      <div className="study-card__meta">
        <span>{t.scopeLabels[currentQuestion.scope]}</span>
        <span>{currentQuestion.points} pkt</span>
        <span>{t.tagLabels[currentQuestion.topic_tag.slug] || currentQuestion.topic_tag.name}</span>
      </div>

      <div className="study-card__media">
        {!currentMediaUrl || !currentMediaType ? (
          <div className="study-card__media-fallback">{copy.mediaUnavailable}</div>
        ) : currentMediaType === 'VIDEO' ? (
          <video
            ref={videoRef}
            src={currentMediaUrl}
            autoPlay
            controls
            playsInline
            preload="auto"
            className="study-card__media-item"
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <img src={currentMediaUrl} alt={currentTranslation.question_text} className="study-card__media-item" />
        )}
      </div>

      <div className="study-card__body">
        <h2>{currentTranslation.question_text}</h2>

        <div className={`study-card__answers ${currentQuestion.type === 'TAK_NIE' ? 'study-card__answers--yes-no' : ''}`}>
          {answerOptions.map((option) => {
            const isSelected = selectedAnswer === option.value;
            const isCorrectOption = answerResult && option.value === currentQuestion.correct_answer;
            const isWrongSelection = answerResult === 'incorrect' && isSelected;
            return (
              <button
                key={option.value}
                type="button"
                disabled={Boolean(answerResult)}
                onClick={() => onAnswer(option.value)}
                className={[
                  'study-card__answer',
                  isSelected ? 'study-card__answer--selected' : '',
                  isCorrectOption ? 'study-card__answer--correct' : '',
                  isWrongSelection ? 'study-card__answer--wrong' : '',
                ].join(' ')}
              >
                {currentQuestion.type === 'ABC' ? `${option.value}. ${option.label}` : option.label}
              </button>
            );
          })}
        </div>

        {answerResult ? (
          <div className={`study-card__result study-card__result--${answerResult}`}>
            <strong>{answerResult === 'correct' ? copy.correct : copy.incorrect}</strong>
            {answerResult === 'incorrect' ? (
              <span>
                {copy.correctAnswer}: {currentQuestion.correct_answer}
                {correctAnswerLabel ? ` - ${correctAnswerLabel}` : ''}
              </span>
            ) : null}
          </div>
        ) : null}

        {answerResult === 'incorrect' ? (
          <button type="button" className="study-card__next" onClick={onNext}>
            {copy.nextQuestion}
          </button>
        ) : null}
      </div>
    </section>
  );
}
